import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Check, Mail } from 'lucide-react';

const plans = [
  {
    id: 'team_starter',
    name: 'Team Starter',
    description: 'For small teams getting started',
    price: 15,
    period: 'member/month',
    billing: 'billed monthly',
    maxMembers: 10,
    features: [
      'All Core individual features',
      'Team health signals dashboard',
      'Auto-generated review summaries',
      'Anonymous team sentiment',
      'Email support'
    ],
    cta: 'Start Team Trial',
    ctaVariant: 'default',
    highlighted: true
  },
  {
    id: 'team_pro',
    name: 'Team Pro',
    description: 'For growing teams with advanced needs',
    price: 12,
    period: 'member/month',
    billing: 'billed annually',
    maxMembers: 50,
    features: [
      'All Pro individual features',
      'Advanced team analytics',
      'Skills gap analysis view',
      'Custom integrations (API)',
      'Dedicated success manager',
      'Priority support'
    ],
    cta: 'Contact Us',
    ctaVariant: 'outline',
    highlighted: false
  }
];

const TeamsPricing = ({ onStartTrial, onContactUs }) => {
  return (
    <section 
      id="pricing"
      className="py-24 bg-slate-950"
      data-testid="teams-pricing"
    >
      <div className="max-w-5xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Team Pricing
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Start with a 28-day free trial. No credit card required.
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative bg-slate-900/50 border-slate-800 ${
                plan.highlighted ? 'ring-2 ring-blue-500/50' : ''
              }`}
              data-testid={`pricing-${plan.id}`}
            >
              {plan.highlighted && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white">
                  Most Popular
                </Badge>
              )}
              
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-white text-2xl">{plan.name}</CardTitle>
                <p className="text-slate-400 text-sm">{plan.description}</p>
              </CardHeader>
              
              <CardContent className="text-center pb-6">
                {/* Price */}
                <div className="mb-2">
                  <span className="text-4xl font-bold text-white">£{plan.price}</span>
                  <span className="text-slate-400">/{plan.period}</span>
                </div>
                <p className="text-slate-500 text-sm mb-2">({plan.billing})</p>
                <p className="text-slate-400 text-sm">Up to {plan.maxMembers} members</p>
                
                {/* Features */}
                <ul className="mt-8 space-y-3 text-left">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              
              <CardFooter>
                <Button 
                  variant={plan.ctaVariant}
                  className={`w-full ${
                    plan.highlighted 
                      ? 'bg-blue-600 hover:bg-blue-500 text-white' 
                      : 'border-slate-700 text-slate-300 hover:bg-slate-800'
                  }`}
                  onClick={plan.id === 'team_starter' ? onStartTrial : onContactUs}
                  data-testid={`${plan.id}-cta`}
                >
                  {plan.id === 'team_pro' && <Mail className="w-4 h-4 mr-2" />}
                  {plan.cta}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Bottom notes */}
        <div className="text-center mt-12 space-y-2">
          <p className="text-slate-400 text-sm">
            28-day free trial. No credit card required.
          </p>
          <p className="text-slate-500 text-sm">
            Minimum 3 team members to see aggregated signals.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TeamsPricing;
